import React, { RefObject, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { tabs, tab, tabPanel } from '@harmony/enablers/react';
import { CSSProperties } from '@harmony/enablers/react/scope-for-react';
import scope from './providers/harmony-scope/provider-harmony-scope';
import PerformanceObserver from './services/performance-observer/service-performance-observer';
import { FilterPanel as FilterPanelv2 } from './features/panel-filters/component-filters-panel';
import { FilterPills } from './features/panel-filters/component-filter-pills';
import { TrackEngagement } from './services/logging/component-track-engagement';
import { useBuildICMProperties, useTrackICMEvent, useTrackInteraction } from './services/logging/hooks';
import {
  addError,
  addNotification,
  clearBannerMessages,
  clearBannerMessagesByType,
  EBannerAppearance,
  EBannerType,
} from './ducks/banner-messages/duck-banner-messages';
import { useAppSelector, useAppDispatch } from './ducks/hooks';
import { endImpersonation } from './ducks/impersonation/duck-impersonation';
import { PageContext } from './utils/page-context';
import { useTabConfig } from './services/tab-configurations/tab-configurations';
import { useShowDataRefreshStatusPanel } from './services/tab-configurations/tab-guards';
import { Badge, Button, ProgressRing, CommandBar, Icon, MessageBar, Option } from './providers/harmony-scope/lazy-enablers';
import { BannerMessagesContainer } from './shared-components/banner-messages-container';
import { EUserRole } from './ducks/api-ducks/participation-details-duck/types-participation-details';
import { ETabId } from './services/tab-configurations/tab-types';
import { setActiveTabFilter } from './ducks/filters/duck-filters';
import { bodyContentStyles, titleCommandBarStyles } from './utils/common-styles';
import { useLCPMarker, useTTIMarker } from './services/performance-observer/hooks';
import { useGetUserNotificationsQuery } from './ducks/api-ducks/user-notifications-duck/api-duck-user-notifications';
import { EICMSeverity, EICMType } from './enums/icm-enum';
import { Flags, useFlagValue } from './services/feature-flags/hooks';
import SellerExperienceHeader from './shared-components/seller-experience-header';
import { EServiceName } from './ducks/api-ducks/types-shared';
import { EUserNotificationStatus, EUserNotificationType } from './ducks/api-ducks/user-notifications-duck/types-user-notifications';
import { StandaloneLayout } from './features/header-standalone/header-standalone';
import { useMEMTranslation } from './utils/text-utils';
import { ButtonWithBadgeWithCount } from './shared-components/badge-with-count';
import { NotificationsPanel } from './features/panel-notifications/component-notifications-panel';
import { DataRefreshPanel } from './features/panel-data-refresh/component-data-refresh-panel';
import { DownloadsPanel } from './features/panel-downloads/component-downloads-panel';
import { closeDownloadPanel } from './ducks/download-panel/download-panel';
import { openDataRefreshPanel as openDataRefreshPanelAction, closeDataRefreshPanel } from './ducks/data-refresh-panel/data-refresh-panel';
import { Flight } from './services/feature-flags/component-flight';
import { useDefaultTab, useGetNotifciationRefreshTime, useShowFilters } from './seller-experience-hooks';
import { SplashScreenNotificationsContainer } from './shared-components/splashscreen-notifications-container';
import { addSplashScreenNotification, resetForImpersonation } from './ducks/splash-screen-notifications/duck-splash-screen-notifications';
import { resolveSplashScreenPhase } from './utils/notification-utils';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { ShowHideEarningsTab } from './features-v2/show-hide-earnings-tab/show-hide-earnings-tab';
import { useOcvSurveyTrigger } from './services/ocv-survey/use-ocv-survey-trigger';
import AppEnvironment from './environment/app-environment';

// We don't lazy load the tabs and text component since they affect the TTI metric.
const Tabs = scope.forReact(tabs);
const Tab = scope.forReact(tab);
const TabPanel = scope.forReact(tabPanel);

const filtersPanelTitle = 'FiltersPanel';
const notificationsPanelTitle = 'NotificationsPanel';
const dataRefreshPanelTitle = 'DataRefreshPanel';
const downloads = 'downloads';
const pageName = 'Seller Experience';

const tabPanelStyle = {
  marginTop: '1.5rem',
};

const newTabLabelStyle = {
  display: 'inline-block',
  position: 'relative' as const,
  whiteSpace: 'nowrap' as const,
};

const newTabBadgeStyle = {
  '--he-spacing-2x-small': '0.0625rem',
  '--he-spacing-x-small': '0.1875rem',
  fontSize: '0.5rem',
  lineHeight: '0.5625rem',
  position: 'absolute',
  right: 0,
  top: 0,
  transform: 'translateY(-0.375rem)',
} as React.CSSProperties & CSSProperties;

const SellerExperience = () => {
  const { t } = useMEMTranslation();
  const tabsRef = useRef(undefined) as unknown as RefObject<tabs | null>;
  const [filtersPanelOpen, setFiltersPanelOpen] = useState<boolean>(false);
  // Removed filterButtonRef, fallback focus logic will use getElementById only
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState<boolean>(false);
  const dataRefreshPanelOpen = useAppSelector((state) => state.dataRefreshPanel.panelOpen);
  const [downloadPanelOpen, setDownloadPanelOpen] = useState<boolean>(false);
  const [filtersAppliedMsg, setFiltersAppliedMsg] = useState<string | null>(null);
  const trackInteraction = useTrackInteraction();
  const filters = useAppSelector((state) => state.filters);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const impersonation = useAppSelector((state) => state.impersonation);
  const appConfig = useAppSelector((state) => state.appConfig);
  // Select download manager state from redux
  const downloadsState = useAppSelector((state) => state.downloadManager);
  const areEarningsVisible = useAppSelector((state) => state.earningsVisibility.areEarningsVisible);
  const dispatch: any = useAppDispatch();
  const tabConfig = useTabConfig();
  const ocvNsatEnabled = useFlagValue(Flags.OCV_NSAT_ENABLED) ?? false;
  useOcvSurveyTrigger(ocvNsatEnabled, AppEnvironment.ocvNsatSurvey);
  const showFilters = useShowFilters();
  const availableTabs = useMemo(() => tabConfig.filter((tabItem) => tabItem.show), [tabConfig]);
  const showMyCompensation = availableTabs.some((tabItem) => tabItem.tabId === ETabId.MyCompensationTab);
  const availableTabOrderKey = availableTabs.map((tabItem) => tabItem.tabId).join('|');
  const defaultTabId = useDefaultTab(availableTabs);
  const [activeTab, setActiveTab] = useState<ETabId>(defaultTabId || availableTabs[0]?.tabId);
  const acFlagEnabled = useFlagValue(Flags.AC_BANNER_NOTIFICATIONS);
  const splashScreenFlagEnabled = useFlagValue(Flags.SPLASH_SCREEN_NOTIFICATIONS);
  const showDataRefreshStatusPanel = useShowDataRefreshStatusPanel();
  const downloadFlagEnabled = useFlagValue(Flags.DOWNLOAD_FLAG) || (impersonation?.isImpersonating && appConfig?.isBusinessPersona);
  const trackICMEvent = useTrackICMEvent();
  const notificationRefreshTime = useGetNotifciationRefreshTime();
  const buildICMProperties = useBuildICMProperties();
  const [earningsVisibilityAnnouncement, setEarningsVisibilityAnnouncement] = useState('');
  const previousEarningsVisibility = useRef<boolean | null>(null);

  useEffect(() => {
    if (!showMyCompensation) {
      previousEarningsVisibility.current = null;
      setEarningsVisibilityAnnouncement('');
      return;
    }

    if (previousEarningsVisibility.current === null) {
      previousEarningsVisibility.current = areEarningsVisible;
      setEarningsVisibilityAnnouncement('');
      return;
    }

    if (previousEarningsVisibility.current !== areEarningsVisible) {
      previousEarningsVisibility.current = areEarningsVisible;
      setEarningsVisibilityAnnouncement(
        t(areEarningsVisible ? 'sellerExperienceEarningsShownAnnouncement' : 'sellerExperienceEarningsHiddenAnnouncement')
      );
    }
  }, [areEarningsVisible, showMyCompensation, t]);

  const fetchACNotifications = useMemo(() => {
    const isBusinessImpersonation = impersonation.isImpersonating && appConfig.isBusinessPersona;
    const isManagerImpersonation = impersonation.isImpersonating && !appConfig.isBusinessPersona;
    const isSellerTerminated = participationDetails?.personnelNumber === Number(participationDetails?.alias);

    return (
      // We check for either the flag or a business impersonation
      (acFlagEnabled || isBusinessImpersonation) &&
      // We then verify that its not a managerImpersonation happening
      !isManagerImpersonation &&
      // If its an impersonation we wait for participant details
      (participationDetails || !isBusinessImpersonation) && // finally we verify that the seller is not terminated
      !isSellerTerminated
    );
  }, [acFlagEnabled, appConfig.isBusinessPersona, impersonation.isImpersonating, participationDetails]);

  const {
    data: userNotificationsData,
    isError: userNotificationsIsError,
    error: userNotificationsError,
  } = useGetUserNotificationsQuery(
    fetchACNotifications
      ? {
          cacheKey: impersonation.impersonatedUser, // Caching key to re-fetch after different impersonations.
          notificationStatus: EUserNotificationStatus.Active, // We fetch only the Active notifications. Expired ones are fetched on demand.
        }
      : skipToken,
    {
      pollingInterval: notificationRefreshTime,
      skipPollingIfUnfocused: true,
    }
  );

  const newNotificationsCount = useMemo(
    () => userNotificationsData?.notifications?.filter((notification) => notification.notificationStatus === EUserNotificationStatus.Active).length,
    [userNotificationsData]
  );

  const planDisabledErrorLogged = useMemo(() => {
    if (filters && participationDetails) {
      const selectedParticipation = participationDetails.coreParticipations.find(
        (participation) => filters.participationId === participation.participationId
      );
      if (selectedParticipation?.isPlanActive) {
        return false;
      }
      if (selectedParticipation?.userRole === EUserRole.NonParticipantManager) {
        return false;
      }
      if (impersonation.isImpersonating) {
        return false;
      }
      // We show the Error whenever the plan is not active or the revenue release is not enabled, its not an NPM and its not impersonating
      return true;
    }
    return false;
  }, [filters, impersonation.isImpersonating, participationDetails]);

  useEffect(() => {
    if (planDisabledErrorLogged) {
      dispatch(
        addError({
          message: t('errorPlanRevenueDisabled'),
          bannerType: EBannerType.PageError,
        })
      );
    }
  }, [dispatch, planDisabledErrorLogged, t]);

  // Get Notifications for user
  useEffect(() => {
    if (userNotificationsIsError) {
      const icmProps = buildICMProperties(EICMType.API, EICMSeverity.Sev4, EServiceName.NOTIFICATION, userNotificationsError);
      trackICMEvent('GetUserNotifications API failed all retries', pageName, '', icmProps);
    }
    if (userNotificationsData && userNotificationsData.notifications && userNotificationsData.notifications.length > 0) {
      userNotificationsData.notifications.forEach((notification) => {
        if (notification.notificationType === EUserNotificationType.ActionCenter || notification.notificationType === EUserNotificationType.Banner) {
          dispatch(
            addNotification({
              notification,
              bannerType: EBannerType.UserNotification,
              appearance: EBannerAppearance.Warning,
            })
          );
        }
        if (splashScreenFlagEnabled && notification.notificationType === EUserNotificationType.SplashScreen) {
          const result = resolveSplashScreenPhase(notification, t('splashScreenOpenMedia'));
          if (result.phase === 'banner') {
            dispatch(
              addNotification({
                notification: result.notification,
                bannerType: EBannerType.UserNotification,
                appearance: EBannerAppearance.Warning,
              })
            );
          } else {
            dispatch(addSplashScreenNotification(notification));
          }
        }
      });
    }
  }, [
    buildICMProperties,
    dispatch,
    splashScreenFlagEnabled,
    t,
    trackICMEvent,
    userNotificationsData,
    userNotificationsError,
    userNotificationsIsError,
  ]);

  // If Available tabs are updated, then set the activeTab to the default tab, if the current tab is not available.
  const defaultTabResolvedFor = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (availableTabs.length === 0 || !defaultTabId) {
      return;
    }

    // The default tab depends on the participation the filters resolve to, because a seller who is
    // pre revenue release or who has not acknowledged their quota is sent to Compliance. Filters
    // arrive after the first tabs do, and they are replaced wholesale when viewing as someone else,
    // so the default has to be resolved again whenever the filters describe a different person.
    // Keying on that person rather than on the impersonation change avoids resolving against the
    // previous user's filters, which are still in the store when the impersonation is recorded.
    const filtersResolvedFor = filters.personnelNumber;
    const isDefaultTabResolved = defaultTabResolvedFor.current === filtersResolvedFor;
    defaultTabResolvedFor.current = filtersResolvedFor;

    // Once resolved, keep the user where they are. Changing filters can add tabs, for example when
    // selecting a fiscal year the user participates in, and that must not navigate them away from a
    // tab that is still available to them.
    const keepActiveTab = isDefaultTabResolved && availableTabs.some((tabItem) => tabItem.tabId === activeTab);
    const nextActiveTab = keepActiveTab ? activeTab : defaultTabId;

    // The active tab selects which filters are sent as query parameters, so it always has to be
    // published to the store, even when the tab the user is on does not change.
    if (filters.activeTab !== nextActiveTab) {
      dispatch(setActiveTabFilter(nextActiveTab));
    }
    if (activeTab !== nextActiveTab) {
      setActiveTab(nextActiveTab);
    }
  }, [activeTab, availableTabs, defaultTabId, dispatch, filters.activeTab, filters.personnelNumber]);

  // Clear banner messages on impersonation change
  useEffect(() => {
    dispatch(clearBannerMessages());
    dispatch(resetForImpersonation());
  }, [impersonation.impersonatedUser, dispatch]);

  // Clear errors whenever a tab change is detected on filters
  useEffect(() => {
    dispatch(clearBannerMessagesByType(EBannerType.PageError));
  }, [dispatch, filters.activeTab]);

  // Open the downloads panel if there is any download of type 'direct' or 'queue' with status 'BEGIN'
  useEffect(() => {
    if (!downloadsState) return;
    // Check direct downloads
    const openPanel = downloadsState.panelOpen;

    if (openPanel) {
      setDownloadPanelOpen(true);
      dispatch(closeDownloadPanel());
    }
  }, [downloadsState, dispatch]);

  const openFilterPanel = () => {
    trackInteraction('Opened filter panel', pageName, 'Filter Panel');
    setFiltersPanelOpen(true);
  };

  const openDownloadsPanel = () => {
    trackInteraction('Opened downloads panel', pageName, 'Downloads Panel');
    setDownloadPanelOpen(true);
  };
  // Focus back to filter button when panel closes, using getElementById fallback only
  useEffect(() => {
    if (!filtersPanelOpen) {
      const fallbackBtn = document.getElementById('OpenFilterPanelButton');
      if (fallbackBtn && typeof fallbackBtn.focus === 'function') {
        fallbackBtn.focus();
      }
    }
  }, [filtersPanelOpen]);

  const openNotificationsPanel = () => {
    trackInteraction('Opened notifications panel', pageName, 'Notifications Panel');
    setNotificationsPanelOpen(true);
  };

  const openDataRefreshPanel = () => {
    trackInteraction('Opened data refresh panel', pageName, 'Data Refresh Panel');
    dispatch(openDataRefreshPanelAction());
  };

  const afterImpersonationBannerHide = () => {
    dispatch(endImpersonation());
    dispatch(clearBannerMessages());
  };

  const tabChanged = (event: CustomEvent<{ activeTab: HTMLElement }>) => {
    if (event.detail.activeTab && event.detail.activeTab.id) {
      // We stop the web performance observer when the tab changes, since an interaction has happened.
      if (PerformanceObserver) {
        PerformanceObserver.cleanup();
      }

      const currTab = event.detail.activeTab.id as ETabId;
      if (availableTabs.map((tTab) => tTab.tabId).includes(currTab)) {
        if (activeTab !== currTab) {
          dispatch(setActiveTabFilter(currTab));
          setActiveTab(currTab);
          trackInteraction('Tab Opened', pageName, currTab);
        }
      }
    }
  };

  // LCP Performance Mark
  useLCPMarker();
  useTTIMarker(availableTabs.length > 0);
  return (
    <FluentProvider theme={webLightTheme}>
      <StandaloneLayout>
        <div
          style={{
            position: 'relative',
            minHeight: '100vh',
          }}
        >
          <div style={{ ...bodyContentStyles, userSelect: 'text' }}>
            <div style={titleCommandBarStyles}>
              <SellerExperienceHeader />
              <CommandBar style={{ width: 'auto', flexGrow: 1 }} actionsPosition="end">
                {showFilters && (
                  <Button id="OpenFilterPanelButton" appearance="command" onClick={openFilterPanel}>
                    <Icon slot="start" name="filter" label="filters" />
                    {t('sellerExperienceFilterButton')}
                  </Button>
                )}
                {showMyCompensation && activeTab === ETabId.MyCompensationTab && <ShowHideEarningsTab />}
                <Flight flag={Flags.NOTIFICATIONS_BELL}>
                  <ButtonWithBadgeWithCount
                    contentNumber={newNotificationsCount}
                    hide={!newNotificationsCount || newNotificationsCount === 0}
                    id="OpenNotificationsPanel"
                    appearance="command"
                    onClick={openNotificationsPanel}
                  >
                    <Icon name="ringer" label={`${t('sellerExperienceBellIconAriaLabel')}: ${newNotificationsCount}`} />
                  </ButtonWithBadgeWithCount>
                </Flight>
                {showFilters && downloadFlagEnabled && (
                  <Button id="OpenPanelButton" appearance="command" label={t('downloadPanelDownloadsHeading')} onClick={openDownloadsPanel}>
                    <Icon name="download" />
                  </Button>
                )}
                {showDataRefreshStatusPanel && (
                  <Button id="OpenDataRefreshPanelButton" appearance="primary" onClick={openDataRefreshPanel} style={{ marginLeft: '0.5rem' }}>
                    {t('dataRefreshStatusButton')}
                  </Button>
                )}
              </CommandBar>
            </div>
            {impersonation.isImpersonating && (
              <MessageBar appearance="info" open onHeMessageBarAfterHide={afterImpersonationBannerHide}>
                {`${t('sellerExperienceImpersonatingString')} ${impersonation.impersonatedUser}`}
              </MessageBar>
            )}
            <BannerMessagesContainer />
            <SplashScreenNotificationsContainer />
            <div style={{ paddingBottom: '1rem' }} />
            {showFilters && <FilterPills />}
          </div>
          <div style={{ ...bodyContentStyles, userSelect: 'text' }}>
            <Tabs
              key={availableTabOrderKey}
              orientation="horizontal"
              size="large"
              onHeChange={tabChanged}
              active-id={activeTab}
              ref={tabsRef}
              manualActivation
            >
              {availableTabs.map((tabItem) => (
                <React.Fragment key={`${tabItem.tabId}-tabKey`}>
                  <Tab id={tabItem.tabId}>
                    {tabItem.isNew ? (
                      <span style={newTabLabelStyle}>
                        <span>{tabItem.tabName}</span>
                        <Badge appearance="accent" pill style={newTabBadgeStyle}>
                          {t('tabNewBadgeLabel')}
                        </Badge>
                      </span>
                    ) : (
                      tabItem.tabName
                    )}
                  </Tab>
                  <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
                    <TabPanel style={tabPanelStyle}>
                      {activeTab === tabItem.tabId && (
                        <PageContext.Provider value={tabItem.tabId}>
                          <TrackEngagement componentName={tabItem.tabId}>{tabItem.component}</TrackEngagement>
                        </PageContext.Provider>
                      )}
                    </TabPanel>
                  </Suspense>
                </React.Fragment>
              ))}
            </Tabs>
          </div>
          {/* This is a hack to pre-fetch the lazy loaded Option component so that it doesnt show blank comboboxes */}
          <Option hidden aria-hidden />
        </div>
        {filters && filtersPanelOpen && (
          <TrackEngagement componentName={filtersPanelTitle}>
            <FilterPanelv2
              closePanel={(applied?: boolean) => {
                if (applied) {
                  setFiltersAppliedMsg(t('ariaFiltersAppliedSuccess'));
                  setTimeout(() => {
                    setFiltersPanelOpen(false);
                    setTimeout(() => setFiltersAppliedMsg(null), 3000);
                  }, 50);
                } else {
                  setFiltersPanelOpen(false);
                }
              }}
            />
          </TrackEngagement>
        )}
        {notificationsPanelOpen && (
          <Flight flag={Flags.NOTIFICATIONS_BELL}>
            <TrackEngagement componentName={notificationsPanelTitle}>
              <NotificationsPanel closePanel={() => setNotificationsPanelOpen(false)} />
            </TrackEngagement>
          </Flight>
        )}
        {dataRefreshPanelOpen && showDataRefreshStatusPanel && (
          <TrackEngagement componentName={dataRefreshPanelTitle}>
            <DataRefreshPanel closePanel={() => dispatch(closeDataRefreshPanel())} />
          </TrackEngagement>
        )}
        {filters && downloadPanelOpen && downloadFlagEnabled && (
          <TrackEngagement componentName={downloads}>
            <DownloadsPanel closePanel={() => setDownloadPanelOpen(false)} />
          </TrackEngagement>
        )}
        {/* ARIA live region for filter applied message - moved to end for priority */}
        <div aria-live="assertive" aria-atomic="true" style={{ position: 'absolute', left: -9999, height: 1, width: 1, overflow: 'hidden' }}>
          {filtersAppliedMsg}
        </div>
        {/* ARIA live region announcing earnings visibility toggles, kept outside the CommandBar so it isn't slotted as a command bar action */}
        <div aria-live="polite" aria-atomic="true" style={{ position: 'absolute', left: -9999, height: 1, width: 1, overflow: 'hidden' }}>
          {earningsVisibilityAnnouncement}
        </div>
      </StandaloneLayout>
    </FluentProvider>
  );
};

export default SellerExperience;
